import { DropdownEditor, Field, TextEditor } from '@approvalmax/ui';
import { constants } from 'modules/common';
import { backend, domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { XeroCurrency } from 'pages/requestForm/data/xero/XeroCurrency';
import { memo, useMemo } from 'react';
import { isSortCodeValid } from 'shared/helpers';

import { Header, Row, RowGroup } from '../../XeroCreateContactPopup.styles';
import { messages } from './FinancialDetailsSection.messages';
import { FinancialDetailsSectionProps } from './FinancialDetailsSection.types';

const { xeroConstants } = constants;

export const FinancialDetailsSection = memo<FinancialDetailsSectionProps>((props) => {
    const { contact, xeroContext, qa, company, showErrors, onChangeReferenceField, onChangeField } = props;

    const currency = useMemo(() => {
        const defaultCurrencyId = contact.defaultCurrency?.id;

        if (!defaultCurrencyId) {
            return null;
        }

        return xeroContext.currencies.find((currency) => currency.id === defaultCurrencyId) ?? null;
    }, [contact.defaultCurrency, xeroContext.currencies]);

    const isUKCompany = company.integration?.countryCode === backend.OrganisationVersion.UK;

    const isInvalidSortCode = Boolean(contact.sortCode && !isSortCodeValid(contact.sortCode));

    const isInvalidBankAccountDetails = Boolean(
        contact.sortCode && (!contact.bankAccountDetails || !/^\d{8}$/.test(contact.bankAccountDetails))
    );

    return (
        <div>
            <Header>{messages.financialDetailsHeader}</Header>

            <RowGroup>
                <Row>
                    <Field title={messages.currencyLabel}>
                        <PlainDataProvider items={xeroContext.currencies}>
                            <DropdownEditor
                                qa={qa('currency-editor')}
                                value={currency}
                                placeholder={messages.dropdownPlaceholder}
                                onChange={(newCurrency: XeroCurrency) =>
                                    onChangeReferenceField('defaultCurrency', newCurrency)
                                }
                            />
                        </PlainDataProvider>
                    </Field>

                    {!company.betaFeatures.includes(domain.CompanyBetaFeature.HideBatchPaymentInContact) && (
                        <>
                            {isUKCompany && (
                                <Field title={messages.sortCodeLabel}>
                                    <TextEditor
                                        qa={qa('batch-payment-editor')}
                                        value={contact.sortCode || ''}
                                        maxLength={xeroConstants.sortCodeMaxLength}
                                        onChange={onChangeField('sortCode')}
                                        placeholder={messages.sortCodePlaceholder}
                                        invalid={showErrors && isInvalidSortCode}
                                    />
                                </Field>
                            )}

                            <Field title={messages.bankAccountNumber}>
                                <TextEditor
                                    qa={qa('batch-payment-editor')}
                                    value={contact.bankAccountDetails || ''}
                                    maxLength={xeroConstants.BANK_ACCOUNT_DETAILS_MAX_LENGTH}
                                    onChange={onChangeField('bankAccountDetails')}
                                    placeholder={messages.bankAccountNumberPlaceholder}
                                    invalid={showErrors && isInvalidBankAccountDetails}
                                />
                            </Field>
                        </>
                    )}
                </Row>
            </RowGroup>
        </div>
    );
});

FinancialDetailsSection.displayName = 'FinancialDetailsSection';
